import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import WarningIcon from "@material-ui/icons/Warning";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { setContactFieldTemplatesCache } from "../../hooks/useContactFieldTemplates";

const MAX_FIELDS = 30;
const ALERT_RED = "#E53935";

// Contato fictício só para a prévia (não vem do banco).
const SAMPLE_CONTACT = {
  name: "Marina Souza",
  number: "+55 11 98888-0000",
  values: {
    alergias: "Ácido salicílico",
    "observações": "Prefere atendimento à tarde",
    "tipo de pele": "Sensível",
    "convênio": "Unimed Plus",
  },
};

const EXAMPLE_FIELDS = [
  { name: "Alergias", isAlert: true },
  { name: "Observações", isAlert: false },
];

const keyOf = (name) => String(name || "").trim().toLowerCase();
let uid = 0;
const withKey = (field) => ({ ...field, _key: `f${++uid}` });
const snapshot = (fields) =>
  JSON.stringify(fields.map(({ id, name, isAlert }) => [id || null, name, Boolean(isAlert)]));

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    grid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)",
      gap: theme.spacing(2),
      alignItems: "start",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    card: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 14,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    cardHead: { padding: theme.spacing(2, 2.25, 0.5) },
    cardTitle: { fontWeight: 700, fontSize: 15 },
    cardHelp: { color: theme.palette.text.secondary, fontSize: 13, lineHeight: 1.5, marginTop: 4 },
    counters: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1), padding: theme.spacing(1.5, 2.25, 0.5) },
    pill: {
      height: 22,
      fontSize: 12,
      fontWeight: 700,
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, dark ? 0.18 : 0.12),
    },
    pillRed: {
      color: dark ? "#e57373" : "#c62828",
      backgroundColor: alpha(ALERT_RED, dark ? 0.18 : 0.12),
    },
    list: { display: "flex", flexDirection: "column", gap: 6, padding: theme.spacing(1.25, 1.5, 0.75) },
    row: {
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
      alignItems: "center",
      gap: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      padding: theme.spacing(0.5, 1, 0.5, 0.5),
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "auto minmax(0, 1fr) auto" },
    },
    rowAlert: { boxShadow: `inset 3px 0 0 ${ALERT_RED}` },
    arrows: { display: "flex", flexDirection: "column" },
    arrowBtn: { padding: 2 },
    usage: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    alertChip: {
      fontWeight: 700,
      height: 28,
      borderColor: theme.palette.divider,
      color: theme.palette.text.secondary,
    },
    alertChipOn: {
      color: dark ? "#e57373" : "#c62828",
      borderColor: ALERT_RED,
      backgroundColor: alpha(ALERT_RED, dark ? 0.18 : 0.1),
      "&:hover, &:focus": { backgroundColor: alpha(ALERT_RED, dark ? 0.26 : 0.16) },
    },
    error: { color: dark ? "#e57373" : "#c62828", fontSize: 12, fontWeight: 700, padding: theme.spacing(0, 2.25, 1) },
    empty: { padding: theme.spacing(1, 2.25), color: theme.palette.text.secondary, fontSize: 13 },
    footer: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: theme.spacing(1), padding: theme.spacing(1, 2.25, 2) },
    unsaved: { color: dark ? "#ffd54f" : "#b26a00", fontSize: 12, fontWeight: 700 },
    save: { marginLeft: "auto" },
    button: { textTransform: "none", fontWeight: 700, borderRadius: 10 },
    tip: {
      margin: theme.spacing(0, 2.25, 2),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 10,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.primary, dark ? 0.06 : 0.04),
    },
    preview: { display: "flex", flexDirection: "column", gap: theme.spacing(2), padding: theme.spacing(1.5, 2.25, 2.25) },
    caption: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      marginBottom: 6,
    },
    chat: { border: `1px solid ${theme.palette.divider}`, borderRadius: 12, overflow: "hidden" },
    chatTop: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.25),
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    avatar: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      flexShrink: 0,
      fontSize: 12,
      fontWeight: 800,
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
    },
    strip: {
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      padding: theme.spacing(0.75, 1.5),
      borderBottom: `1px solid ${ALERT_RED}`,
      backgroundColor: alpha(ALERT_RED, 0.1),
    },
    stripIcon: { color: ALERT_RED, fontSize: 20, flexShrink: 0 },
    stripList: { display: "flex", flexWrap: "wrap", columnGap: 16, rowGap: 2 },
    stripName: { fontWeight: 700, color: ALERT_RED },
    noAlert: { padding: theme.spacing(1, 1.5), fontSize: 12.5, color: theme.palette.text.secondary, borderBottom: `1px solid ${theme.palette.divider}` },
    bubble: {
      margin: theme.spacing(1.25, 1.5),
      maxWidth: "80%",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "4px 12px 12px 12px",
      padding: theme.spacing(1, 1.25),
      fontSize: 13,
    },
    profile: { border: `1px solid ${theme.palette.divider}`, borderRadius: 12, padding: theme.spacing(0.5, 1.5) },
    profRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 0),
      fontSize: 13,
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&:last-child": { borderBottom: "none" },
    },
    profLabel: { color: theme.palette.text.secondary },
    profValue: { fontWeight: 700, textAlign: "right", minWidth: 0, overflowWrap: "anywhere" },
    profAlert: { color: dark ? "#e57373" : "#c62828", fontWeight: 800 },
  };
});

/**
 * Configuração do modelo da ficha do contato: campos que aparecem para todos
 * os contatos e quais são de alerta (destacados em vermelho na ficha e no chat).
 * A coluna da direita mostra, ao vivo, como fica para um contato de exemplo.
 */
const ContactFieldTemplatesManager = () => {
  const classes = useStyles();
  const [fields, setFields] = useState([]);
  const [saved, setSaved] = useState(snapshot([]));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const lastInput = useRef(null);

  const load = (data) => {
    const list = (data || []).map(withKey);
    setFields(list);
    setSaved(snapshot(list));
  };

  useEffect(() => {
    api
      .get("/contact-field-templates", { params: { usage: "true" } })
      .then(({ data }) => load(data))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, []);

  const updateField = (key, changes) =>
    setFields((prev) => prev.map((f) => (f._key === key ? { ...f, ...changes } : f)));

  const move = (index, delta) =>
    setFields((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const remove = (key) => setFields((prev) => prev.filter((f) => f._key !== key));

  const problem = useMemo(() => {
    const seen = new Set();
    for (let i = 0; i < fields.length; i += 1) {
      const key = keyOf(fields[i].name);
      if (!key) return `O campo ${i + 1} está sem nome.`;
      if (seen.has(key)) return `O nome "${fields[i].name.trim()}" está repetido.`;
      seen.add(key);
    }
    return "";
  }, [fields]);

  const dirty = snapshot(fields) !== saved;
  const alertCount = fields.filter((f) => f.isAlert).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/contact-field-templates", {
        fields: fields.map(({ id, name, isAlert }) => ({ id, name, isAlert })),
      });
      load(data);
      setContactFieldTemplatesCache(data);
      toast.success("Ficha do contato salva.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setFields((prev) => [...prev, withKey({ name: "", isAlert: false })]);
    setTimeout(() => lastInput.current && lastInput.current.focus(), 0);
  };

  // Prévia: o que o contato de exemplo mostraria com o modelo em edição.
  const filled = fields
    .map((f) => ({ name: f.name.trim(), isAlert: f.isAlert, value: SAMPLE_CONTACT.values[keyOf(f.name)] }))
    .filter((f) => f.name && f.value);
  const previewAlerts = filled.filter((f) => f.isAlert);
  const previewRows = [...previewAlerts, ...filled.filter((f) => !f.isAlert)];
  const modelNames = new Set(fields.map((f) => keyOf(f.name)));
  const freeRows = Object.entries(SAMPLE_CONTACT.values).filter(([name]) => !modelNames.has(name));

  if (loading) return <CircularProgress size={24} />;

  return (
    <div className={classes.grid}>
      <section className={classes.card} aria-label="Campos da ficha">
        <div className={classes.cardHead}>
          <Typography className={classes.cardTitle}>Campos da ficha</Typography>
          <Typography className={classes.cardHelp}>
            Estes campos aparecem na ficha de todos os contatos da empresa. Campos de alerta
            preenchidos ficam em vermelho na ficha e no topo do chat.
          </Typography>
        </div>

        <div className={classes.counters}>
          <Chip size="small" className={classes.pill} label={`${fields.length} de ${MAX_FIELDS} campos`} />
          <Chip
            size="small"
            className={`${classes.pill} ${classes.pillRed}`}
            label={`${alertCount} ${alertCount === 1 ? "campo de alerta" : "campos de alerta"}`}
          />
        </div>

        <div className={classes.list}>
          {fields.length === 0 && (
            <Typography className={classes.empty}>
              Nenhum campo ainda.{" "}
              <Button
                size="small"
                color="primary"
                className={classes.button}
                onClick={() => setFields(EXAMPLE_FIELDS.map(withKey))}
              >
                Usar exemplo (Alergias, Observações)
              </Button>
            </Typography>
          )}

          {fields.map((field, index) => (
            <div
              className={`${classes.row} ${field.isAlert ? classes.rowAlert : ""}`}
              key={field._key}
            >
              <div className={classes.arrows}>
                <IconButton
                  size="small"
                  className={classes.arrowBtn}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={`Subir ${field.name || "campo"}`}
                >
                  <ArrowUpwardIcon style={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  className={classes.arrowBtn}
                  disabled={index === fields.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={`Descer ${field.name || "campo"}`}
                >
                  <ArrowDownwardIcon style={{ fontSize: 16 }} />
                </IconButton>
              </div>

              <TextField
                value={field.name}
                onChange={(e) => updateField(field._key, { name: e.target.value })}
                inputRef={index === fields.length - 1 ? lastInput : undefined}
                placeholder="Nome do campo"
                inputProps={{ maxLength: 100, "aria-label": `Nome do campo ${index + 1}` }}
                variant="outlined"
                margin="dense"
                size="small"
                style={{ margin: 0 }}
              />

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className={classes.usage}>
                  {field.id ? `${field.contactsCount ?? 0} ${field.contactsCount === 1 ? "contato" : "contatos"}` : "novo"}
                </span>
                <Chip
                  clickable
                  variant="outlined"
                  size="small"
                  label="Alerta"
                  icon={field.isAlert ? <WarningIcon style={{ color: ALERT_RED }} /> : undefined}
                  className={`${classes.alertChip} ${field.isAlert ? classes.alertChipOn : ""}`}
                  onClick={() => updateField(field._key, { isAlert: !field.isAlert })}
                  aria-pressed={Boolean(field.isAlert)}
                />
              </div>

              <Tooltip title="Remover">
                <IconButton size="small" onClick={() => remove(field._key)} aria-label={`Remover ${field.name || "campo"}`}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>

        {problem && <div className={classes.error} role="alert">{problem}</div>}

        <div className={classes.footer}>
          <Button
            variant="outlined"
            color="primary"
            className={classes.button}
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={fields.length >= MAX_FIELDS}
          >
            Adicionar campo
          </Button>
          {dirty && <span className={classes.unsaved}>Alterações não salvas</span>}
          <Button
            variant="contained"
            color="primary"
            className={`${classes.button} ${classes.save}`}
            onClick={handleSave}
            disabled={saving || !dirty || Boolean(problem)}
          >
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </div>

        <div className={classes.tip}>
          <strong>O que acontece ao mexer:</strong> renomear um campo atualiza o nome em todos os
          contatos que já o preenchem. Remover um campo daqui não apaga o que foi preenchido; o
          valor continua no contato como informação livre.
        </div>
      </section>

      <section className={classes.card} aria-label="Prévia da ficha">
        <div className={classes.cardHead}>
          <Typography className={classes.cardTitle}>Prévia com um contato de exemplo</Typography>
          <Typography className={classes.cardHelp}>Atualiza enquanto você edita.</Typography>
        </div>
        <div className={classes.preview}>
          <div>
            <div className={classes.caption}>No chat do atendimento</div>
            <div className={classes.chat}>
              <div className={classes.chatTop}>
                <div className={classes.avatar}>MS</div>
                <div>
                  <Typography style={{ fontSize: 13, fontWeight: 700 }}>{SAMPLE_CONTACT.name}</Typography>
                  <Typography color="textSecondary" style={{ fontSize: 11.5 }}>{SAMPLE_CONTACT.number}</Typography>
                </div>
              </div>
              {previewAlerts.length ? (
                <div className={classes.strip} role="alert">
                  <WarningIcon className={classes.stripIcon} />
                  <div className={classes.stripList}>
                    {previewAlerts.map((alert) => (
                      <Typography key={alert.name} variant="body2" component="span">
                        <span className={classes.stripName}>{alert.name}:</span> {alert.value}
                      </Typography>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={classes.noAlert}>
                  Nenhum alerta preenchido para este contato. A faixa vermelha não aparece.
                </div>
              )}
              <div className={classes.bubble}>Oi, queria marcar o retorno da semana que vem.</div>
            </div>
          </div>

          <div>
            <div className={classes.caption}>Na ficha do contato</div>
            <div className={classes.profile}>
              {previewRows.map((row) => (
                <div className={classes.profRow} key={row.name}>
                  <span className={`${classes.profLabel} ${row.isAlert ? classes.profAlert : ""}`}>{row.name}</span>
                  <span className={`${classes.profValue} ${row.isAlert ? classes.profAlert : ""}`}>{row.value}</span>
                </div>
              ))}
              {freeRows.map(([name, value]) => (
                <div className={classes.profRow} key={name}>
                  <span className={classes.profLabel}>{name} (informação livre)</span>
                  <span className={classes.profValue}>{value}</span>
                </div>
              ))}
              {!previewRows.length && !freeRows.length && (
                <div className={classes.profRow}>
                  <span className={classes.profLabel}>Sem campos preenchidos.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactFieldTemplatesManager;
